import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ArrowLeft, Brain, Building2 } from 'lucide-react';

const fmt = (v) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
const tip = { background: '#fff', border: '1px solid #E2DDD3', borderRadius: 8, fontSize: 12, fontFamily: 'Inter, system-ui, sans-serif', boxShadow: '0 4px 12px rgba(0,0,0,0.04)', padding: '8px 12px' };

function Gauge({ value, max = 100, label, color = '#1F3A34', size = 100 }) {
  const r = (size - 14) / 2, circ = 2 * Math.PI * r, prog = Math.min(Math.max(value / max, 0), 1);
  return (
    <div className="flex flex-col items-center gap-1">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#E3DED6" strokeWidth="3" />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="3"
          strokeDasharray={circ} strokeDashoffset={circ * (1 - prog)} strokeLinecap="round"
          transform={`rotate(-90 ${size/2} ${size/2})`} className="transition-all duration-700" />
        <text x={size/2} y={size/2+2} textAnchor="middle" fill="#1C1C1C" fontSize="15" fontFamily="Inter, system-ui, sans-serif" fontWeight="300">
          {typeof value === 'number' ? (value >= 10 ? value.toFixed(1) : value.toFixed(2)) : value}
        </text>
      </svg>
      <p className="text-[10px] text-[#8C877F] font-sans uppercase tracking-wide">{label}</p>
    </div>
  );
}

export default function AssetDetail() {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [advisory, setAdvisory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    api.get(`/assets/${assetId}`).then(({ data }) => setAsset(data)).catch(console.error).finally(() => setLoading(false));
  }, [assetId]);

  const runAI = async () => {
    setAiLoading(true);
    try { const { data } = await api.post('/ai/advisory', { asset_id: assetId }); setAdvisory(data); } catch (e) { console.error(e); } finally { setAiLoading(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><p className="text-sm text-[#8C877F] animate-pulse">Loading...</p></div>;
  if (!asset) return <div className="text-center py-20 text-[#8C877F]">Asset not found</div>;

  const m = asset.metrics;
  const metrics = [
    { l: 'Market Value', v: fmt(m.market_value) }, { l: 'Purchase Price', v: fmt(m.purchase_price) }, { l: 'Annual Rent', v: fmt(m.annual_rent) },
    { l: 'Gross Yield', v: `${m.gross_yield}%`, a: true }, { l: 'Net Yield', v: `${m.net_yield}%`, a: true }, { l: 'IRR', v: `${m.irr}%`, a: true },
    { l: 'Cash-on-Cash', v: `${m.cash_on_cash}%` }, { l: 'Equity Multiple', v: `${m.equity_multiple}x` }, { l: 'DSCR', v: m.dscr.toFixed(2) },
    { l: 'LTV', v: `${m.ltv}%` }, { l: 'Loan Amount', v: fmt(m.loan_amount) }, { l: 'Net Cashflow', v: fmt(m.net_cashflow) },
  ];

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex items-center gap-4">
        <button data-testid="back-to-dashboard" onClick={() => navigate('/dashboard')} className="text-[#8C877F] hover:text-[#1F3A34] transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded bg-[#1F3A34]/[0.06] flex items-center justify-center"><Building2 className="w-5 h-5 text-[#1F3A34]/50" /></div>
          <div>
            <h1 className="font-serif text-2xl md:text-3xl text-[#1C1C1C] font-medium">{asset.name}</h1>
            <p className="text-[12px] text-[#8C877F] font-sans">{asset.address}{asset.city ? `, ${asset.city}` : ''}</p>
          </div>
        </div>
      </div>

      <div className="flex justify-center gap-8 py-2">
        <Gauge value={m.irr} max={25} label="IRR %" />
        <Gauge value={m.ltv} max={100} label="LTV %" color="#8C877F" />
        <Gauge value={m.dscr} max={3} label="DSCR" color={m.dscr >= 1.2 ? '#2E6F57' : '#8A3C3C'} />
      </div>

      <Tabs defaultValue="performance" className="w-full">
        <TabsList className="bg-[#F4F1EB] border border-[#E2DDD3] rounded p-1 h-auto">
          {['performance', 'debt', 'history', 'ai-advice'].map(t => (
            <TabsTrigger key={t} value={t} className="text-[12px] font-sans rounded data-[state=active]:bg-white data-[state=active]:text-[#1F3A34] data-[state=active]:shadow-sm px-4 py-2">
              {t === 'performance' ? 'Performance' : t === 'debt' ? 'Debt & Loan' : t === 'history' ? 'History' : 'AI Advice'}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="performance" className="mt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-4">
            {metrics.map(({ l, v, a }) => (
              <div key={l} className="py-2 border-b border-[#F4F1EB]">
                <p className="text-[10px] text-[#8C877F] font-sans uppercase tracking-wide">{l}</p>
                <p className={`text-[15px] font-sans ${a ? 'text-[#1F3A34]' : 'text-[#1C1C1C]'}`}>{v}</p>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="debt" className="mt-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-4">
            {[
              { l: 'Loan Amount', v: fmt(m.loan_amount) }, { l: 'Interest Rate', v: `${m.interest_rate}%` }, { l: 'Term', v: `${m.loan_term} years` },
              { l: 'Annual Debt Service', v: fmt(m.annual_debt_service) }, { l: 'LTV', v: `${m.ltv}%` }, { l: 'DSCR', v: m.dscr.toFixed(2) },
            ].map(({ l, v }) => (
              <div key={l} className="py-2 border-b border-[#F4F1EB]">
                <p className="text-[10px] text-[#8C877F] font-sans uppercase tracking-wide">{l}</p>
                <p className="text-[15px] font-sans text-[#1C1C1C]">{v}</p>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="history" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-[#E2DDD3] rounded-xl p-5">
              <p className="text-[12px] text-[#8C877F] mb-3">Value & Equity — 5 Year</p>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={asset.history || []}>
                  <defs><linearGradient id="ag" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#1F3A34" stopOpacity={0.08} /><stop offset="95%" stopColor="#1F3A34" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid stroke="#E3DED6" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: '#8C877F', fontSize: 9 }} axisLine={false} tickLine={false} interval={11} />
                  <YAxis tick={{ fill: '#8C877F', fontSize: 9 }} axisLine={false} tickLine={false} tickFormatter={v => `${(v/1e3).toFixed(0)}k`} />
                  <Tooltip contentStyle={tip} formatter={v => [fmt(v), '']} />
                  <Area type="monotone" dataKey="value" stroke="#D9D3C8" strokeWidth={1} fill="none" />
                  <Area type="monotone" dataKey="equity" stroke="#1F3A34" strokeWidth={1.5} fill="url(#ag)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white border border-[#E2DDD3] rounded-xl p-5">
              <p className="text-[12px] text-[#8C877F] mb-3">Cashflow — 5 Year</p>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={asset.history || []}>
                  <defs><linearGradient id="cg" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#1F3A34" stopOpacity={0.08} /><stop offset="95%" stopColor="#1F3A34" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid stroke="#E3DED6" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: '#8C877F', fontSize: 9 }} axisLine={false} tickLine={false} interval={11} />
                  <YAxis tick={{ fill: '#8C877F', fontSize: 9 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={tip} formatter={v => [fmt(v), '']} />
                  <Area type="monotone" dataKey="cashflow" stroke="#1F3A34" strokeWidth={1.5} fill="url(#cg)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="ai-advice" className="mt-6">
          <div className="bg-white border border-[#E2DDD3] rounded-xl p-6 space-y-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Brain className="w-5 h-5 text-[#1F3A34]" /><p className="text-[13px] text-[#1F3A34] font-sans font-medium">AI Capital Advisory</p></div>
              <Button data-testid="run-ai-advisory" onClick={runAI} disabled={aiLoading}
                className="bg-[#1F3A34] text-white hover:bg-[#285047] text-[12px] px-4 py-2 rounded font-sans">
                {aiLoading ? 'Analyzing...' : 'Run Analysis'}
              </Button>
            </div>
            {advisory ? (
              <div className="space-y-4 animate-fade-in">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { l: 'Performance', v: advisory.performance_score },
                    { l: 'Risk', v: advisory.risk_score },
                    { l: 'Bias', v: advisory.sell_hold_bias, badge: true },
                    { l: 'Confidence', v: `${advisory.confidence}%` },
                  ].map(i => (
                    <div key={i.l}>
                      <p className="text-[10px] text-[#8C877F] uppercase tracking-wide">{i.l}</p>
                      {i.badge ? (
                        <Badge className={`mt-1 rounded text-[11px] ${i.v === 'HOLD' ? 'bg-[#E6EFEA] text-[#1F3A34] border-[#2E6F57]/20' : i.v === 'SELL' ? 'bg-[#FFF5F5] text-[#8A3C3C] border-[#8A3C3C]/20' : 'bg-[#FFF8F0] text-[#B8860B] border-[#B8860B]/20'}`}>{i.v}</Badge>
                      ) : <p className="text-xl font-serif text-[#1C1C1C] font-medium">{i.v}</p>}
                    </div>
                  ))}
                </div>
                <div className="border-t border-[#F4F1EB] pt-4 space-y-2">
                  <p className="text-[14px] text-[#1C1C1C] font-serif italic leading-relaxed">{advisory.analysis}</p>
                  <p className="text-[12px] text-[#8C877F]">{advisory.refinance_advice}</p>
                </div>
                {advisory.recommendations?.length > 0 && (
                  <div className="space-y-2 pt-2">{advisory.recommendations.map((r, i) => (
                    <p key={i} className="text-[12px] text-[#1C1C1C] pl-3 border-l-2 border-[#1F3A34]/20">{r}</p>
                  ))}</div>
                )}
              </div>
            ) : <p className="text-[12px] text-[#8C877F]">Run analysis to generate AI insights for this asset.</p>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
